import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import { Globe2, LogOut, MessageSquare, LayoutDashboard, Sparkles, ShieldCheck } from "lucide-react";

export default function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isLanding = loc.pathname === "/";

  const handleLogout = () => { logout(); nav("/"); };

  return (
    <header className="mec-glass sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <div className="relative w-9 h-9 rounded-md bg-[#0F172A] flex items-center justify-center">
            <Globe2 className="w-5 h-5 text-white" strokeWidth={2} />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#004EEB] pulse-ring" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-bold text-base text-slate-900">{t("brand.name")}</span>
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#004EEB]">{t("brand.suffix")}</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm">
          <Link to="/jobs" className="text-slate-600 hover:text-slate-900 transition" data-testid="nav-jobs">{t("header.jobs")}</Link>
          {user?.role === "employer" && (
            <Link to="/candidates" className="text-slate-600 hover:text-slate-900 transition" data-testid="nav-candidates">{t("header.candidates")}</Link>
          )}
          {user?.role === "candidate" && (
            <Link to="/ai-assistant" className="text-slate-600 hover:text-slate-900 transition flex items-center gap-1" data-testid="nav-ai">
              <Sparkles className="w-4 h-4" /> {t("header.ai_assistant")}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user ? (
            <>
              <Link to="/dashboard" className="hidden sm:block">
                <Button variant="ghost" size="sm" data-testid="nav-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-1.5" /> {t("header.dashboard")}
                </Button>
              </Link>
              {user.is_admin && (
                <Link to="/admin/employers" className="hidden sm:block">
                  <Button variant="ghost" size="sm" className="text-amber-700 hover:bg-amber-50" data-testid="nav-admin">
                    <ShieldCheck className="w-4 h-4 mr-1.5" /> {t("header.admin")}
                  </Button>
                </Link>
              )}
              <Link to="/messages" className="hidden sm:block">
                <Button variant="ghost" size="icon" data-testid="nav-messages">
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-1.5" /> {t("header.logout")}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="login-link">{t("header.login")}</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="register-link">
                  {t("header.register")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
